import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import api from '@/admin/lib/api'

export interface PulseEventType {
  id: string // the event_key, e.g. "claim.created"
  object: 'event_type'
  label: string
  description: string
  family: string // "A" lifecycle, "B" workflow, etc.
  status: string
  payload_schema: Record<string, unknown>
}

interface ListResult<T> {
  count: number
  page: number
  page_size: number
  next: string | null
  previous: string | null
  results: T[]
}

// Catalog of event types this tenant can subscribe to. Drives the
// multi-select on the webhook create form. Tenant-admin endpoint;
// mirrors the public-facing /api/v1/events/ but with session auth.
export function usePulseEventTypes() {
  return useQuery<PulseEventType[]>({
    queryKey: ['pulse-event-types'],
    queryFn: () =>
      api
        .get('/admin/pulse/events/')
        .then((r) => (r.data as ListResult<PulseEventType>).results),
    staleTime: 5 * 60_000, // catalog is stable; 5 min cache is generous
  })
}

// ── Entity grouping ────────────────────────────────────────────────
//
// Group event types by their leading entity prefix (claim.*, holding.*,
// ...). Shared by the webhook create page (event picker) and the
// catalog viewer page.

const ENTITY_ORDER: readonly { key: string; label: string }[] = [
  { key: 'claim', label: 'Claim' },
  { key: 'claim_payment', label: 'Claim payment' },
  { key: 'holding', label: 'Holding' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'inspection', label: 'Inspection' },
  { key: 'workflow', label: 'Workflow' },
  { key: 'consumer', label: 'Consumer' },
  { key: 'organisation', label: 'Organisation' },
  { key: 'system', label: 'System' },
]

function entityFor(eventKey: string): { key: string; label: string } {
  const prefix = eventKey.split('.')[0] ?? 'other'
  const match = ENTITY_ORDER.find((e) => e.key === prefix)
  return (
    match ?? {
      key: prefix,
      label: prefix.charAt(0).toUpperCase() + prefix.slice(1),
    }
  )
}

export interface EventGroup {
  key: string
  label: string
  events: PulseEventType[]
}

export function groupEventsByEntity(events: PulseEventType[]): EventGroup[] {
  const buckets = new Map<string, EventGroup>()
  for (const evt of events) {
    const entity = entityFor(evt.id)
    if (!buckets.has(entity.key)) {
      buckets.set(entity.key, { ...entity, events: [] })
    }
    buckets.get(entity.key)!.events.push(evt)
  }
  // Preferred entity order first; then anything else alphabetically.
  const ordered: EventGroup[] = []
  for (const { key } of ENTITY_ORDER) {
    if (buckets.has(key)) {
      ordered.push(buckets.get(key)!)
      buckets.delete(key)
    }
  }
  const rest = Array.from(buckets.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
  return [...ordered, ...rest]
}

// Resolver hook: returns a (event_key) -> human label function backed
// by the catalog. Falls back to the raw key if the catalog hasn't
// loaded or the key isn't found (e.g. tenant-defined event keys that
// aren't in the platform catalog).
export function useEventTypeLabel() {
  const events = usePulseEventTypes()
  return useMemo(() => {
    const byKey = new Map<string, string>()
    for (const e of events.data ?? []) byKey.set(e.id, e.label)
    return (eventKey: string) => byKey.get(eventKey) ?? eventKey
  }, [events.data])
}
